import { Ionicons } from '@expo/vector-icons';
import { useQuery } from '@tanstack/react-query';
import React, { useState } from 'react';
import { Linking, Pressable, ScrollView, Switch, View } from 'react-native';
import { useTranslation } from 'react-i18next';

import { AppText } from '@/components/ui/AppText';
import { BackHeader } from '@/components/ui/BackHeader';
import { Card } from '@/components/ui/Card';
import { EmptyState } from '@/components/ui/EmptyState';
import { Screen } from '@/components/ui/Screen';
import { useToast } from '@/components/ui/Toast';
import {
  BellIcon,
  ClockIcon,
  FootballIcon,
  StarIcon,
  WhistleIcon,
  type IconProps,
} from '@/components/ui/icons';
import { isSupabaseConfigured } from '@/config';
import { useThemePalette } from '@/config/theme';
import { useAuth } from '@/lib/auth/AuthProvider';
import {
  getMyPushPrefs,
  notificationsAuthorized,
  pushAvailability,
  registerForPush,
  sendTestNotification,
  setMyPushPrefs,
  type PushPrefs,
} from '@/lib/notifications';

const DEFAULT_PREFS: PushPrefs = {
  goalNotifications: true,
  goalFollowedOnly: false,
  fulltimeAlerts: true,
  prematchReminders: true,
  deadlineReminders: true,
  playerRatingAlerts: true,
};

function SectionLabel({ label }: { label: string }) {
  return (
    <AppText
      variant="micro"
      weight="bold"
      className="mb-2 px-4 uppercase tracking-widest text-muted"
    >
      {label}
    </AppText>
  );
}

/**
 * A labelled toggle row. `nested` indents the row and drops the leading icon
 * disc so a dependent sub-setting (e.g. "My teams only") reads as a child of
 * the row above it rather than a peer.
 */
function ToggleRow({
  icon,
  label,
  hint,
  value,
  onToggle,
  nested = false,
  last = false,
}: {
  icon?: React.ComponentType<IconProps>;
  label: string;
  hint?: string;
  value: boolean;
  onToggle: (next: boolean) => void;
  nested?: boolean;
  last?: boolean;
}) {
  const palette = useThemePalette();
  const Icon = icon;
  return (
    <View
      className={`flex-row items-center gap-3 py-3.5 ${nested ? 'ps-14 pe-4 bg-inset/40' : 'px-4'} ${
        last ? '' : 'border-b border-line'
      }`}
    >
      {Icon && !nested ? (
        <View className="h-9 w-9 items-center justify-center rounded-full bg-inset">
          <Icon size={17} color={value ? palette.voltBright : palette.soft} />
        </View>
      ) : null}
      <View className="flex-1">
        <AppText variant="caption" weight="medium" className="text-ink">
          {label}
        </AppText>
        {hint ? (
          <AppText variant="micro" className="mt-0.5 leading-4 text-muted">
            {hint}
          </AppText>
        ) : null}
      </View>
      <Switch
        value={value}
        onValueChange={onToggle}
        accessibilityLabel={label}
        trackColor={{ false: palette.inset, true: palette.voltDim }}
        thumbColor={value ? palette.volt : palette.muted}
        ios_backgroundColor={palette.inset}
      />
    </View>
  );
}

/** Notification preferences — choose which push alerts you receive. */
export default function NotificationsScreen() {
  const palette = useThemePalette();
  const { t } = useTranslation();
  const { userId } = useAuth();
  const toast = useToast();

  const prefsQuery = useQuery({
    queryKey: ['push-prefs', userId ?? 'anon'],
    queryFn: getMyPushPrefs,
    enabled: isSupabaseConfigured && Boolean(userId),
    staleTime: 60_000,
  });

  // Whether the OS will actually deliver pushes. Drives the "turned off in
  // system settings" banner so the toggles never look live while the phone
  // silently drops them.
  const osAuthQuery = useQuery({
    queryKey: ['notif-os-auth'],
    queryFn: notificationsAuthorized,
    enabled: pushAvailability() === 'available',
    staleTime: 30_000,
  });

  // Local override layered over the server read so a flipped switch stays put
  // even if the persist round-trips slowly (or can't, before push is set up).
  const [localPrefs, setLocalPrefs] = useState<PushPrefs | null>(null);
  const serverPrefs: PushPrefs = prefsQuery.data
    ? {
        goalNotifications: prefsQuery.data.goalNotifications,
        goalFollowedOnly: prefsQuery.data.goalFollowedOnly,
        fulltimeAlerts: prefsQuery.data.fulltimeAlerts,
        prematchReminders: prefsQuery.data.prematchReminders,
        deadlineReminders: prefsQuery.data.deadlineReminders,
        playerRatingAlerts: prefsQuery.data.playerRatingAlerts,
      }
    : DEFAULT_PREFS;
  const prefs = localPrefs ?? serverPrefs;

  const setPref = (key: keyof PushPrefs, next: boolean) => {
    const updated: PushPrefs = { ...prefs, [key]: next };
    setLocalPrefs(updated); // optimistic — we never flick it back
    // Persist best-effort. A device that can't obtain a push token yet (FCM not
    // configured, permission denied, emulator) must NOT cause the switch to
    // revert — that reads as "the toggles don't work". setMyPushPrefs writes the
    // single changed column when a token row exists; registerForPush upserts a
    // fresh row (with the full prefs) for a device that has none.
    void (async () => {
      try {
        await setMyPushPrefs({ [key]: next });
      } catch {
        // No row yet (or signed-out mid-flight): fall through to registration.
      }
      try {
        if (pushAvailability() === 'available') await registerForPush(updated);
      } catch {
        // Token registration can fail before FCM is configured; ignore it.
      }
    })();
  };

  if (!userId) {
    return (
      <Screen>
        <BackHeader title={t('notifSettings.title')} />
        <EmptyState
          icon="notifications-off-outline"
          title={t('notifSettings.guestTitle')}
          body={t('notifSettings.guestBody')}
        />
      </Screen>
    );
  }

  const availability = pushAvailability();
  const osDisabled = availability === 'available' && osAuthQuery.data === false;

  return (
    <Screen>
      <BackHeader title={t('notifSettings.title')} />
      <ScrollView contentContainerStyle={{ paddingTop: 6, paddingBottom: 36 }}>
        <AppText variant="caption" className="mb-4 px-4 leading-5 text-muted">
          {t('notifSettings.subtitle')}
        </AppText>

        {/* OS-level permission is off: alerts are saved but won't show until the
            user re-enables notifications in system settings. */}
        {osDisabled ? (
          <Pressable
            accessibilityRole="button"
            onPress={() => {
              void Linking.openSettings();
            }}
            className="mx-4 mb-4 flex-row items-start gap-3 rounded-card border border-volt-line bg-volt-glass px-4 py-3 active:opacity-80"
          >
            <Ionicons name="notifications-off-outline" size={18} color={palette.voltBright} />
            <View className="flex-1">
              <AppText variant="caption" weight="bold" className="text-ink">
                {t('notifSettings.osDisabledTitle')}
              </AppText>
              <AppText variant="micro" className="mt-0.5 leading-5 text-soft">
                {t('notifSettings.osDisabledBody')}
              </AppText>
              <AppText variant="micro" weight="bold" className="mt-1.5 text-volt-bright">
                {t('notifSettings.osDisabledAction')}
              </AppText>
            </View>
          </Pressable>
        ) : null}

        {/* This device has no push token yet (push needs its final FCM setup).
            Without this, the toggles look active but nothing can be delivered. */}
        {prefsQuery.data && !prefsQuery.data.registered && !osDisabled ? (
          <View className="mx-4 mb-4 flex-row items-start gap-3 rounded-card border border-volt-line bg-volt-glass px-4 py-3">
            <Ionicons name="information-circle-outline" size={18} color={palette.voltBright} />
            <AppText variant="micro" className="flex-1 leading-5 text-soft">
              {t('notifSettings.settingUp')}
            </AppText>
          </View>
        ) : null}

        {/* Prove notifications display on this device right now — goal / full-time
            alerts only arrive on a real match event. */}
        <Pressable
          accessibilityRole="button"
          onPress={() => {
            void sendTestNotification(t('notifications.testTitle'), t('notifications.testBody'));
            toast.show({ message: t('notifications.testSent'), tone: 'success' });
          }}
          className="mx-4 mb-5 flex-row items-center gap-3 rounded-card border border-line bg-surface px-4 py-3.5 active:opacity-80"
        >
          <View className="h-9 w-9 items-center justify-center rounded-full bg-volt-glass">
            <BellIcon size={18} color={palette.voltBright} />
          </View>
          <View className="flex-1">
            <AppText variant="caption" weight="bold" className="text-ink">
              {t('notifications.testTitle2')}
            </AppText>
            <AppText variant="micro" className="text-muted">
              {t('notifications.testHint')}
            </AppText>
          </View>
          <Ionicons name="chevron-forward" size={16} color={palette.muted} />
        </Pressable>

        <SectionLabel label={t('notifSettings.sectionMatch')} />
        <Card className="mx-4 mb-5 overflow-hidden">
          <ToggleRow
            icon={FootballIcon}
            label={t('notifSettings.goalLabel')}
            hint={t('notifSettings.goalHint')}
            value={prefs.goalNotifications}
            onToggle={(next) => setPref('goalNotifications', next)}
          />
          {/* "My teams only" is a child of Goal alerts: only meaningful while
              goals are on, so it appears nested directly beneath. */}
          {prefs.goalNotifications ? (
            <ToggleRow
              label={t('notifSettings.followedOnlyLabel')}
              hint={t('notifSettings.followedOnlyHint')}
              value={prefs.goalFollowedOnly}
              onToggle={(next) => setPref('goalFollowedOnly', next)}
              nested
            />
          ) : null}
          <ToggleRow
            icon={WhistleIcon}
            label={t('notifSettings.fulltimeLabel')}
            hint={t('notifSettings.fulltimeHint')}
            value={prefs.fulltimeAlerts}
            onToggle={(next) => setPref('fulltimeAlerts', next)}
          />
          <ToggleRow
            icon={ClockIcon}
            label={t('notifSettings.reminderLabel')}
            hint={t('notifSettings.reminderHint')}
            value={prefs.prematchReminders}
            onToggle={(next) => setPref('prematchReminders', next)}
          />
          <ToggleRow
            icon={StarIcon}
            label={t('notifSettings.ratingLabel')}
            hint={t('notifSettings.ratingHint')}
            value={prefs.playerRatingAlerts}
            onToggle={(next) => setPref('playerRatingAlerts', next)}
            last
          />
        </Card>

        <SectionLabel label={t('notifSettings.sectionFantasy')} />
        <Card className="mx-4 mb-5 overflow-hidden">
          <ToggleRow
            icon={ClockIcon}
            label={t('notifSettings.deadlineLabel')}
            hint={t('notifSettings.deadlineHint')}
            value={prefs.deadlineReminders}
            onToggle={(next) => setPref('deadlineReminders', next)}
            last
          />
        </Card>

        <SectionLabel label={t('notifications.widgets')} />
        <Card className="mx-4 mb-5 overflow-hidden">
          <View className="flex-row items-start gap-3 px-4 py-3.5">
            <View className="h-9 w-9 items-center justify-center rounded-full bg-volt-glass">
              <Ionicons name="phone-portrait-outline" size={17} color={palette.voltBright} />
            </View>
            <View className="flex-1 gap-1">
              <AppText variant="caption" weight="bold" className="text-ink">
                {t('notifications.widgetMatchTitle')}
              </AppText>
              <AppText variant="micro" className="leading-5 text-muted">
                {t('notifications.widgetMatchBody')}
              </AppText>
              <AppText variant="micro" className="mt-1 leading-5 text-soft">
                {t('notifications.widgetHowTo')}
              </AppText>
            </View>
          </View>
        </Card>

        {availability !== 'available' ? (
          <View className="mx-4 rounded-card border border-line bg-surface px-4 py-3">
            <AppText variant="micro" className="leading-5 text-muted">
              {t('profile.pushHint')}
            </AppText>
          </View>
        ) : null}
      </ScrollView>
    </Screen>
  );
}
